import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Globe, ShoppingCart, Settings, ArrowLeft, Phone, ShoppingBag, RefreshCw, AlertTriangle, ChevronDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCardStatusBadge } from '../utils/cardStatus';
import OTPVerificationModal from './OTPVerificationModal';
import { SESSION_KEY } from '../helper/common';


const config = {
  apiUrl: process.env.REACT_APP_API_URL_User,
  currency: 'Rs',
};

const CardManagement = () => {
  // Current settings (fetched from backend, true = enabled = N, false = disabled = Y)
  const [atmActivated, setAtmActivated] = useState(true);
  const [internationalEnabled, setInternationalEnabled] = useState(false);
  const [ecommerceEnabled, setEcommerceEnabled] = useState(true);
  const [mototransactionEnabled, setMototransactionEnabled] = useState(false);
  const [postransactionEnabled, setPostransactionEnabled] = useState(false);
  const [recurringtransactionEnabled, setRecurringtransactionEnabled] = useState(false);
  const [excessLimitEnabled, setExcessLimitEnabled] = useState(false);

  // Track changed settings
  const [changedSettings, setChangedSettings] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [cardNumber, setCardNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOTPModal, setShowOTPModal] = useState(false);

  // Supplementary cards state
  const [cardList, setCardList] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);

  const navigate = useNavigate();

  // Get card number and fetch settings
  useEffect(() => {
    const storedCardNumber = localStorage.getItem('cardNumber');
    const custId = localStorage.getItem('custId');

    if (storedCardNumber && storedCardNumber !== 'N/A') {
      setCardNumber(storedCardNumber);
      fetchCardList(custId, storedCardNumber);
    } else {
      setLoading(false);
      toast.error('No card number found. Please visit dashboard first.');
    }
  }, []);

  // Fetch supplementary cards list
  const fetchCardList = async (custId, primaryCardNumber) => {
    try {
      const token = localStorage.getItem(SESSION_KEY);
      const custId = localStorage.getItem('custId');
      const response = await axios.get(`${config.apiUrl}/api/user/list`, {
        params: { custId },
        headers: {'Authorization': `Bearer ${token}`}
      });

      const cardsData = response.data.cards || [];
      setCardList(cardsData);

      // Set primary card as default selected
      const primaryCard = cardsData.find(card => card.cardNumber === primaryCardNumber) || cardsData[0];
      if (primaryCard) {
        setSelectedCard(primaryCard);
        fetchCardSettings(primaryCard.cardNumber);
      }
    } catch (err) {
      console.error('Error fetching card list:', err);
      toast.error('Failed to load card list');
      // Fallback to primary card if list fetch fails
      fetchCardSettings(primaryCardNumber);
    }
  };

  // Handle card selection from dropdown
  const handleCardChange = (event) => {
    const selectedCardNumber = event.target.value;
    const card = cardList.find(c => c.cardNumber === selectedCardNumber);

    if (card) {
      setSelectedCard(card);
      setCardNumber(card.cardNumber);
      setChangedSettings({}); // Clear any unsaved changes
      fetchCardSettings(card.cardNumber);
    }
  };

  // const fetchCardSettings = async (cardNum) => {
  //   try {
  //     setLoading(true);
  //     const response = await fetch(`${config.apiUrl}/api/cards/settings?cardNumber=${cardNum}`);

  //     if (!response.ok) throw new Error("Failed to fetch settings");
  //     //old
  //     const jsonResponse = await response.json(); // Parse as JSON first
  //     const xmlText = jsonResponse.value;
  //     const parser = new DOMParser();
  //     const xmlDoc = parser.parseFromString(xmlText, "text/xml");

  //     const cardAtmChkInd = xmlDoc.getElementsByTagName("cardAtmChkInd")[0]?.textContent;
  //     const cardIntlChkInd = xmlDoc.getElementsByTagName("cardIntlChkInd")[0]?.textContent;
  //     const cardEcomChkInd = xmlDoc.getElementsByTagName("cardEcommChkInd")[0]?.textContent;
  //     const cardMotoChkInd = xmlDoc.getElementsByTagName("cardMotoChkInd")[0]?.textContent;
  //     const cardPosChkInd = xmlDoc.getElementsByTagName("cardPosChkInd")[0]?.textContent;
  //     const cardRecurrChkInd = xmlDoc.getElementsByTagName("cardRecurrChkInd")[0]?.textContent;
  //     const exceedLimitInd = xmlDoc.getElementsByTagName("exceedLimitInd")[0]?.textContent;

  //     // Update state: N = enabled (true), Y = disabled (false)
  //     setAtmActivated(cardAtmChkInd === "N");
  //     setInternationalEnabled(cardIntlChkInd === "N");
  //     setEcommerceEnabled(cardEcomChkInd === "N");
  //     setMototransactionEnabled(cardMotoChkInd === "N");
  //     setPostransactionEnabled(cardPosChkInd === "N");
  //     setRecurringtransactionEnabled(cardRecurrChkInd === "N");
  //     setExcessLimitEnabled(exceedLimitInd === "Y"); // Changed to N for enabled
  //   } catch (error) {
  //     console.error('Error fetching card settings:', error);
  //     toast.error("Failed to load card settings");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchCardSettings = async (cardNum) => {
    try {
      setLoading(true);
      const token = localStorage.getItem(SESSION_KEY);
      const response = await axios.get(`${config.apiUrl}/api/cards/settings`, {
        params: { cardNumber: cardNum },
        headers: { 'Authorization': `Bearer ${token}`},
      });

      const jsonResponse = response.data;
      const xmlText = jsonResponse.value;
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, "text/xml");

      const cardAtmChkInd = xmlDoc.getElementsByTagName("cardAtmChkInd")[0]?.textContent;
      const cardIntlChkInd = xmlDoc.getElementsByTagName("cardIntlChkInd")[0]?.textContent;
      const cardEcomChkInd = xmlDoc.getElementsByTagName("cardEcommChkInd")[0]?.textContent;
      const cardMotoChkInd = xmlDoc.getElementsByTagName("cardMotoChkInd")[0]?.textContent;
      const cardPosChkInd = xmlDoc.getElementsByTagName("cardPosChkInd")[0]?.textContent;
      const cardRecurrChkInd = xmlDoc.getElementsByTagName("cardRecurrChkInd")[0]?.textContent;
      const exceedLimitInd = xmlDoc.getElementsByTagName("exceedLimitInd")[0]?.textContent;

      // Update state: N = enabled (true), Y = disabled (false)
      setAtmActivated(cardAtmChkInd === "N");
      setInternationalEnabled(cardIntlChkInd === "N");
      setEcommerceEnabled(cardEcomChkInd === "N");
      setMototransactionEnabled(cardMotoChkInd === "N");
      setPostransactionEnabled(cardPosChkInd === "N");
      setRecurringtransactionEnabled(cardRecurrChkInd === "N");
      setExcessLimitEnabled(exceedLimitInd === "Y");
    } catch (error) {
      console.error('Error fetching card settings:', error);
      toast.error("Failed to load card settings");
    } finally {
      setLoading(false);
    }
  };

  // Handle toggle for changed settings
  const handleToggle = (settingKey, currentValue, setter) => {
    let newBackendValue;

    if (settingKey === 'exceedLimitInd') {
      // Excess limit ka alag logic: Y = enabled, N = disabled
      newBackendValue = !currentValue ? "Y" : "N";
    } else {
      // Baaki settings ka normal logic: N = enabled, Y = disabled  
      newBackendValue = !currentValue ? "N" : "Y";
    }

    setChangedSettings((prev) => ({
      ...prev,
      [settingKey]: newBackendValue // Use the correctly calculated value
    }));
    setter(!currentValue); // Update UI immediately (will revert if save fails)
  };

  // const updateExceedLimit = async (newValue) => {
  //   if (!cardNumber) {
  //     toast.error("Card number is required");
  //     return false;
  //   }

  //   try {
  //     const payload = {
  //       cardNumber,
  //       exceedLimitInd: newValue
  //     };

  //     const response = await fetch(`${config.apiUrl}/api/cards/updateExceedLimit`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(payload)
  //     });

  //     const result = await response.json();


  //     // Check backend message to decide success
  //     if (response.ok && result.respDetail?.includes("Update Success")) {
  //       toast.success("Excess limit setting updated successfully!"); // Hardcoded for real success
  //       return true;
  //     } else {
  //       // Show backend message for failures
  //       const errorMessage = result.respDetail || result.error || "Failed to update excess limit setting";
  //       toast.error(errorMessage);
  //       return false;
  //     }
  //   } catch (error) {
  //     toast.error("Network error updating excess limit setting");
  //     return false;
  //   }
  // };

  const updateExceedLimit = async (newValue) => {
    if (!cardNumber) {
      toast.error("Card number is required");
      return false;
    }

    try {
      const token = localStorage.getItem(SESSION_KEY);
      const payload = {
        cardNumber,
        exceedLimitInd: newValue
      };

      const response = await axios.post(`${config.apiUrl}/api/cards/updateExceedLimit`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`

        },
      });

      const result = response.data;


      if (result.respDetail?.includes("Update Success")) {
        toast.success("Excess limit setting updated successfully!");
        return true;
      } else {
        const errorMessage = result.respDetail || result.error || "Failed to update excess limit setting";
        toast.error(errorMessage);
        return false;
      }
    } catch (error) {
      toast.error("Network error updating excess limit setting");
      return false;
    }
  };

  // Update other card settings
  // const updateCardSettings = async (settingsPayload) => {
  //   if (!cardNumber) {
  //     toast.error("Card number is required");
  //     return false;
  //   }

  //   try {
  //     const payload = {
  //       cardNumber,
  //       settings: settingsPayload
  //     };

  //     const response = await fetch(`${config.apiUrl}/api/cards/updateSettings`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(payload)
  //     });

  //     const result = await response.json();

  //     if (response.ok && result.success) {
  //       toast.success("Card settings updated successfully!");
  //       return true;
  //     } else {
  //       toast.error(result.error || "Failed to update card settings");
  //       return false;
  //     }
  //   } catch (error) {
  //     toast.error("Network error updating card settings");
  //     return false;
  //   }
  // };

  const updateCardSettings = async (settingsPayload) => {
    if (!cardNumber) {
      toast.error("Card number is required");
      return false;
    }

    try {
      const token = localStorage.getItem(SESSION_KEY);

      const payload = {
        cardNumber,
        settings: settingsPayload
      };

      const response = await axios.post(`${config.apiUrl}/api/cards/updateSettings`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`

        },
      });

      const result = response.data;

      if (result.success) {
        toast.success("Card settings updated successfully!");
        return true;
      } else {
        toast.error(result.error || "Failed to update card settings");
        return false;
      }
    } catch (error) {
      toast.error("Network error updating card settings");
      return false;
    }
  };

  // Save all settings – actual execution (called after OTP verified)
  const executeSaveAll = async () => {
    if (!cardNumber) {
      toast.error("Card number is required");
      return;
    }

    setIsUpdating(true);

    try {
      let allSuccess = true;

      // Separate exceedLimitInd from other settings
      const { exceedLimitInd, ...otherSettings } = changedSettings;

      // Update exceedLimitInd separately if changed
      if (exceedLimitInd !== undefined) {
        // console.log('Calling updateExceedLimit with:', exceedLimitInd);
        const success = await updateExceedLimit(exceedLimitInd);
        if (!success) {
          allSuccess = false;
          // Revert UI change on error
          setExcessLimitEnabled(exceedLimitInd === "N" ? true : false);
        }
      }

      // Update other settings if there are any
      if (Object.keys(otherSettings).length > 0) {
        const settingsPayload = {
          cardAtmChkInd: otherSettings.cardAtmChkInd ?? (atmActivated ? "N" : "Y"),
          cardIntlChkInd: otherSettings.cardIntlChkInd ?? (internationalEnabled ? "N" : "Y"),
          cardEcommChkInd: otherSettings.cardEcomChkInd ?? (ecommerceEnabled ? "N" : "Y"),
          cardMotoChkInd: otherSettings.cardMotoChkInd ?? (mototransactionEnabled ? "N" : "Y"),
          cardPosChkInd: otherSettings.cardPosChkInd ?? (postransactionEnabled ? "N" : "Y"),
          cardRecurrChkInd: otherSettings.cardRecurrChkInd ?? (recurringtransactionEnabled ? "N" : "Y"),
          exceedLimitInd: exceedLimitInd ?? (excessLimitEnabled ? "Y" : "N")
        };

        // console.log('Calling updateCardSettings with:', settingsPayload);
        const success = await updateCardSettings(settingsPayload);

        if (!success) {
          allSuccess = false;
          // Revert UI changes on error
          if (otherSettings.cardAtmChkInd !== undefined) setAtmActivated(otherSettings.cardAtmChkInd === "N");
          if (otherSettings.cardIntlChkInd !== undefined) setInternationalEnabled(otherSettings.cardIntlChkInd === "N");
          if (otherSettings.cardEcomChkInd !== undefined) setEcommerceEnabled(otherSettings.cardEcomChkInd === "N");
          if (otherSettings.cardMotoChkInd !== undefined) setMototransactionEnabled(otherSettings.cardMotoChkInd === "N");
          if (otherSettings.cardPosChkInd !== undefined) setPostransactionEnabled(otherSettings.cardPosChkInd === "N");
          if (otherSettings.cardRecurrChkInd !== undefined) setRecurringtransactionEnabled(otherSettings.cardRecurrChkInd === "N");
        }
      }

      if (allSuccess) {
        // Clear changed settings on success
        setChangedSettings({});
      }
    } catch (error) {
      console.error('Error in handleSaveAll:', error);
      // Revert all UI changes on network error
      if (changedSettings.cardAtmChkInd !== undefined) setAtmActivated(changedSettings.cardAtmChkInd === "N");
      if (changedSettings.cardIntlChkInd !== undefined) setInternationalEnabled(changedSettings.cardIntlChkInd === "N");
      if (changedSettings.cardEcomChkInd !== undefined) setEcommerceEnabled(changedSettings.cardEcomChkInd === "N");
      if (changedSettings.cardMotoChkInd !== undefined) setMototransactionEnabled(changedSettings.cardMotoChkInd === "N");
      if (changedSettings.cardPosChkInd !== undefined) setPostransactionEnabled(changedSettings.cardPosChkInd === "N");
      if (changedSettings.cardRecurrChkInd !== undefined) setRecurringtransactionEnabled(changedSettings.cardRecurrChkInd === "N");
      if (changedSettings.exceedLimitInd !== undefined) setExcessLimitEnabled(changedSettings.exceedLimitInd === "Y");

      toast.error("Network error updating settings");
    } finally {
      setIsUpdating(false);
    }
  };

  // Validate then show OTP modal before saving
  const handleSaveAll = () => {
    if (!cardNumber) {
      toast.error("Card number is required");
      return;
    }
    if (Object.keys(changedSettings).length === 0) {
      toast.info("No settings changed");
      return;
    }
    setShowOTPModal(true);
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const ToggleCard = ({ icon: Icon, title, description, enabled, onToggle, isLoading, color = 'green', delay = 0 }) => {
    const colorClasses = {
      green: { bg: 'bg-green-100', darkBg: 'dark:bg-green-900/50', text: 'text-green-700', darkText: 'dark:text-green-400' },
      amber: { bg: 'bg-amber-100', darkBg: 'dark:bg-amber-900/50', text: 'text-amber-700', darkText: 'dark:text-amber-400' },
      yellow: { bg: 'bg-yellow-100', darkBg: 'dark:bg-yellow-900/50', text: 'text-yellow-700', darkText: 'dark:text-yellow-400' },
      blue: { bg: 'bg-blue-100', darkBg: 'dark:bg-blue-900/50', text: 'text-blue-700', darkText: 'dark:text-blue-400' },
      purple: { bg: 'bg-purple-100', darkBg: 'dark:bg-purple-900/50', text: 'text-purple-700', darkText: 'dark:text-purple-400' },
      indigo: { bg: 'bg-indigo-100', darkBg: 'dark:bg-indigo-900/50', text: 'text-indigo-700', darkText: 'dark:text-indigo-400' },
      red: { bg: 'bg-red-100', darkBg: 'dark:bg-red-900/50', text: 'text-red-700', darkText: 'dark:text-red-400' }
    };

    const currentColor = colorClasses[color] || colorClasses.green;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -3, transition: { duration: 0.2 } }}
        transition={{ duration: 0.4, delay }}
        className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-slate-200/50 hover:shadow-xl hover:bg-white/90 transition-all duration-300 dark:bg-gray-800/80 dark:border-gray-700/50 dark:hover:bg-gray-800/90"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className={`p-3 rounded-xl ${currentColor.bg} ${currentColor.darkBg} shadow-sm`}>
              <Icon className={`h-6 w-6 ${currentColor.text} ${currentColor.darkText}`} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{title}</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{description}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <span className={`text-sm font-medium ${enabled ? 'text-green-700 dark:text-green-400' : 'text-slate-500 dark:text-slate-400'}`}>
              {enabled ? 'Enabled' : 'Disabled'}
            </span>
            <button
              onClick={onToggle}
              disabled={isLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-700 dark:focus:ring-green-400 focus:ring-offset-2 ${enabled ? 'bg-green-700 dark:bg-green-600' : 'bg-slate-300 dark:bg-gray-600'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} shadow-sm`}
              role="switch"
              aria-checked={enabled}
              aria-label={`Toggle ${title}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
              />
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading card settings...</div>
      </div>
    );
  }

  if (!cardNumber) {
    return (
      <section id="management" className="py-16 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 rounded-xl p-8 max-w-md mx-auto">
              <CreditCard className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-800 dark:text-red-200 mb-2">No Card Found</h3>
              <p className="text-red-700 dark:text-red-300 mb-4">
                Please visit the dashboard first to load your card information.
              </p>
              <button
                onClick={handleGoToDashboard}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <OTPVerificationModal
        isOpen={showOTPModal}
        actionLabel="Save Settings"
        onSuccess={() => {
          setShowOTPModal(false);
          executeSaveAll();
        }}
        onCancel={() => setShowOTPModal(false)}
      />
      <section id="management" className="py-16 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4 bg-gradient-to-r from-green-600 to-green-700 dark:from-green-400 dark:to-green-500 bg-clip-text text-transparent">Manage Transaction</h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto text-lg">
              Manage Transaction
              {cardNumber && <span className="block mt-2 text-sm">Customer Card: •••• {cardNumber.slice(-4)}</span>}
            </p>
          </motion.div>

          {/* Card Selection Dropdown */}
          {cardList.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="max-w-4xl mx-auto mb-8"
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-slate-200/50 dark:bg-gray-800/80 dark:border-gray-700/50">
                <label htmlFor="card-select" className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
                  Select Card
                </label>
                <div className="relative">
                  <select
                    id="card-select"
                    value={selectedCard?.cardNumber || ''}
                    onChange={handleCardChange}
                    className="w-full appearance-none bg-white dark:bg-gray-700 border border-slate-300 dark:border-gray-600 rounded-xl px-4 py-3 pr-10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-400 focus:border-transparent transition-all duration-200 cursor-pointer"
                  >
                    {cardList.map((card) => (
                      <option key={card.cardNumber} value={card.cardNumber}>
                        {card.cardholderName} (•••• {card.cardNumber.slice(-4)})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                </div>
                {selectedCard && (
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">
                      Card Type: <span className="font-semibold text-slate-900 dark:text-white">
                        {selectedCard.isPrimary ? 'Primary Card' : 'Supplementary Card'}
                      </span>
                    </span>
                    {(() => {
                      const statusBadge = getCardStatusBadge(selectedCard.status);
                      const StatusIcon = statusBadge.icon;

                      return (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center ${statusBadge.classes}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusBadge.label}
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          <div className="max-w-4xl mx-auto space-y-6">
            <ToggleCard
              icon={CreditCard}
              title="ATM Transactions"
              description="Enable or disable ATM withdrawals and cash advances. When disabled, your card cannot be used at ATMs for cash transactions."
              enabled={atmActivated}
              onToggle={() => handleToggle('cardAtmChkInd', atmActivated, setAtmActivated)}
              isLoading={isUpdating}
              color="green"
              delay={0}
            />
            <ToggleCard
              icon={Globe}
              title="International Transactions"
              description="Allow transactions outside your home country. Disable this feature to block all international purchases and enhance security while traveling."
              enabled={internationalEnabled}
              onToggle={() => handleToggle('cardIntlChkInd', internationalEnabled, setInternationalEnabled)}
              isLoading={isUpdating}
              color="amber"
              delay={0.08}
            />
            <ToggleCard
              icon={ShoppingCart}
              title="E-commerce Transactions"
              description="Control online shopping and MOTO (Mail Order/Telephone Order) transactions. When disabled, your card cannot be used for online purchases or phone/mail orders."
              enabled={ecommerceEnabled}
              onToggle={() => handleToggle('cardEcomChkInd', ecommerceEnabled, setEcommerceEnabled)}
              isLoading={isUpdating}
              color="yellow"
              delay={0.16}
            />
            <ToggleCard
              icon={Phone}
              title="MOTO Transactions"
              description="Control Mail Order/Telephone Order (MOTO) transactions. Disable this option to block card-not-present purchases made via phone or mail channels."
              enabled={mototransactionEnabled}
              onToggle={() => handleToggle('cardMotoChkInd', mototransactionEnabled, setMototransactionEnabled)}
              isLoading={isUpdating}
              color="blue"
              delay={0.24}
            />
            <ToggleCard
              icon={ShoppingBag}
              title="POS Transactions"
              description="Control point-of-sale transactions. When disabled, your card cannot be used for in-store purchases at physical terminals."
              enabled={postransactionEnabled}
              onToggle={() => handleToggle('cardPosChkInd', postransactionEnabled, setPostransactionEnabled)}
              isLoading={isUpdating}
              color="purple"
              delay={0.32}
            />
            <ToggleCard
              icon={RefreshCw}
              title="Recurring Transactions"
              description="Card-Not-Present Transaction / Periodic Payment as Agreed Between a Cardholder and a Company Providing Goods/Services."
              enabled={recurringtransactionEnabled}
              onToggle={() => handleToggle('cardRecurrChkInd', recurringtransactionEnabled, setRecurringtransactionEnabled)}
              isLoading={isUpdating}
              color="indigo"
              delay={0.40}
            />
            <ToggleCard
              icon={AlertTriangle}
              title="Excess Over Limit"
              description="Allow transactions that exceed your credit limit. When disabled, transactions will be declined if they would put you over your credit limit."
              enabled={excessLimitEnabled}
              onToggle={() => handleToggle('exceedLimitInd', excessLimitEnabled, setExcessLimitEnabled)}
              isLoading={isUpdating}
              color="red"
              delay={0.48}
            />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="bg-green-50/80 dark:bg-green-900/20 backdrop-blur-sm border border-green-200/50 dark:border-green-700/50 rounded-xl p-6"
            >
              <div className="flex items-start space-x-3">
                <Settings className="h-5 w-5 text-red-700 dark:text-red-400 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">Disclaimer</h4>
                  <p className="text-sm text-green-700 dark:text-green-300 leading-relaxed">
                    Changes to your card settings take effect after clicking "Save All". You'll receive SMS and email notifications
                    for all setting modifications. If you didn't make these changes, please contact customer support immediately.
                    <span className="block mt-2 font-medium">Note: Please ensure E-commerce and International transactions are enabled as per MCB's recommended settings for secure online and international purchases.</span>
                  </p>
                </div>
              </div>
            </motion.div>

            <div className="flex justify-center space-x-4 mt-8">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </button>
              <button
                onClick={handleSaveAll}
                disabled={isUpdating || Object.keys(changedSettings).length === 0}
                className={`flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors ${isUpdating || Object.keys(changedSettings).length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span>Save All</span>
                {isUpdating && <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin"></div>}
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CardManagement;